import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useUnsavedGuard } from '@/shared/useUnsavedGuard';
import { SubmitButton } from './SubmitButton';

export interface ConfirmPanelProps {
  /** Names the object ("Delete note “Rent”?"). */
  title: ReactNode;
  /** States what the action does — what changes, and whether it can be undone. */
  consequence: ReactNode;
  /** The action's verb, named ("Delete note"). */
  confirmLabel: string;
  /** "Cancel" by default; a "Keep …" label when the action is itself a cancel. */
  cancelLabel?: string | undefined;
  /** The confirm is `error`-coloured instead of primary (ACT-003). */
  destructive?: boolean | undefined;
  /** In flight: the confirm shows its loading state and dismissal is blocked. */
  busy?: boolean | undefined;
  /** The action's error, shown once, inside the dialog (TMM-B3). */
  error?: ReactNode | undefined;
  /** The person types this word to enable the confirm. */
  requireText?: string | undefined;
  /** The typed field's label; required with `requireText`. */
  requireTextLabel?: string | undefined;
  /** Disables the confirm (e.g. a body field is not yet valid). */
  confirmDisabled?: boolean | undefined;
  /** Drops the confirm — the body offers a different next step (e.g. the Subscribe card). */
  hideConfirm?: boolean | undefined;
  /** The body slot: form fields or follow-up content below the consequence. */
  children?: ReactNode;
  /** The title element's id, for a host `Dialog`'s `aria-labelledby`. */
  titleId?: string | undefined;
  /** The consequence element's id, for a host `Dialog`'s `aria-describedby`. */
  consequenceId?: string | undefined;
  /** Visual subtitle under the title. The consequence stays the description. */
  subtitle?: ReactNode;
  /**
   * Focus the heading instead of Cancel. In-drawer confirms use this; `ConfirmDialog` does not
   * (DLG-004 keeps Cancel focused).
   */
  focusHeading?: boolean | undefined;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * The confirmation's title, body and actions — `ConfirmDialog`'s content, and what a dialog
 * renders in place of its own content to confirm an action inside it (dialogs never stack,
 * DLG-005). The host routes `Esc` to `onCancel` (unless `busy`), points its
 * `aria-labelledby` / `aria-describedby` at `titleId` / `consequenceId`, and returns focus to
 * the opener on dismiss (`useConfirm`'s opener key).
 */
export function ConfirmPanel({
  title,
  consequence,
  confirmLabel,
  cancelLabel,
  destructive = false,
  busy = false,
  error,
  requireText,
  requireTextLabel,
  confirmDisabled = false,
  hideConfirm = false,
  children,
  titleId,
  consequenceId,
  subtitle,
  focusHeading = false,
  onConfirm,
  onCancel,
}: ConfirmPanelProps) {
  const { t } = useTranslation();
  const [typed, setTyped] = useState('');
  const textMatches = requireText === undefined || typed === requireText;
  const dismissRef = useRef<HTMLButtonElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const alertRef = useRef<HTMLDivElement>(null);
  const hasError = error !== undefined && error !== null && error !== false;
  const hadError = useRef(hasError);

  // Focus lands on the safe choice when the confirmation appears (DLG-004) — also when it
  // replaces a dialog's content, where the focused opener has just unmounted.
  useEffect(() => {
    if (focusHeading) headingRef.current?.focus();
    else dismissRef.current?.focus();
  }, [focusHeading]);

  // A failure that appears moves focus to it (A11Y-010): the confirm that was just pressed is
  // enabled again, and a screen reader would otherwise hear nothing. It fires on the error
  // appearing, never on a re-render and never for an error present at mount.
  useEffect(() => {
    if (hasError && !hadError.current) alertRef.current?.focus();
    hadError.current = hasError;
  }, [hasError]);

  return (
    <>
      <DialogTitle id={titleId} ref={headingRef} tabIndex={focusHeading ? -1 : undefined}>
        {title}
      </DialogTitle>
      {subtitle}
      <DialogContent>
        <Stack spacing={2}>
          <DialogContentText id={consequenceId}>{consequence}</DialogContentText>
          {children}
          {requireText !== undefined && (
            <TextField
              label={requireTextLabel}
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              disabled={busy}
              autoComplete="off"
              fullWidth
            />
          )}
          {hasError && (
            <Alert ref={alertRef} severity="error" tabIndex={-1}>
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {hideConfirm ? (
          <Button ref={dismissRef} variant="contained" onClick={onCancel} disabled={busy}>
            {t('common.done')}
          </Button>
        ) : (
          <Button ref={dismissRef} onClick={onCancel} disabled={busy}>
            {cancelLabel ?? t('common.cancel')}
          </Button>
        )}
        {!hideConfirm && (
          <SubmitButton
            color={destructive ? 'error' : 'primary'}
            loading={busy}
            disabled={confirmDisabled || !textMatches}
            onClick={onConfirm}
          >
            {confirmLabel}
          </SubmitButton>
        )}
      </DialogActions>
    </>
  );
}

export interface ConfirmDialogProps extends Omit<ConfirmPanelProps, 'titleId' | 'consequenceId'> {
  /** Defaults to open — most callers mount it only while a confirmation is pending. */
  open?: boolean | undefined;
  /**
   * Whether a page editor's unsaved-changes question closes this confirmation first (DLG-005,
   * TMMAPP-0097 §C 26). Default true; the question's own dialog (`UnsavedChangesDialog`) is false.
   */
  dismissOnLeave?: boolean | undefined;
}

/**
 * The one confirmation contract (TMM-B4, TMMAPP-0071): the title names the object, the body
 * states the consequence, the confirm names the action. `Esc` and Cancel dismiss unless `busy`;
 * focus returns to the opener on close (DLG-004). Every irreversible action confirms here;
 * `ConfirmButton` is only for reversible, low-stakes ones.
 */
export function ConfirmDialog({
  open = true,
  dismissOnLeave = true,
  ...panelProps
}: ConfirmDialogProps) {
  const titleId = useId();
  const consequenceId = useId();
  const { busy = false, onCancel } = panelProps;
  // An open confirmation has nothing unsaved; when a page editor's question is about to show, it
  // closes (as Cancel) so the question never opens on top of it.
  useUnsavedGuard(false, {
    busy,
    dismissWhenClean: open && dismissOnLeave && !busy ? onCancel : undefined,
  });
  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onCancel}
      aria-labelledby={titleId}
      aria-describedby={consequenceId}
      fullWidth
      maxWidth="xs"
    >
      <ConfirmPanel {...panelProps} titleId={titleId} consequenceId={consequenceId} />
    </Dialog>
  );
}
